import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { FiFileText, FiCalendar, FiUsers, FiSettings, FiRefreshCw, FiTrash2, FiUpload, FiPlay } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import LLMSelector from '../JobPost/LLMSelector';
import toast from 'react-hot-toast';

const MeetingMinutesForm = ({ onGenerate, isGenerating, onClear }) => {
  const [selectedModel, setSelectedModel] = useState('gpt-4');
  const [fileContent, setFileContent] = useState('');
  
  const { register, handleSubmit, formState: { errors }, watch, reset, setValue } = useForm({
    defaultValues: {
      transcript: '',
      meetingTitle: '',
      meetingDate: '',
      attendees: '',
      tone: 'formal',
      includeActionItems: true,
      includeSummary: true,
      language: 'english',
      notes: ''
    }
  });

  const watchedValues = watch();
  const hasContent = watchedValues.transcript?.trim() || fileContent?.trim();

  const sampleTranscript = `AI Advisory Committee Meeting - AI Pilot Project Review
Date: January 15, 2025
Time: 2:00 PM - 3:30 PM
Location: City Hall Conference Room A

Attendees:
- Sarah Chen (Committee Chair, Chief Information Officer)
- Dr. Michael Rodriguez (AI Ethics Advisor)
- Jennifer Walsh (Director of Digital Services)
- Alex Thompson (IT Security Manager)
- Maria Santos (Public Services Director)
- David Kim (Legal Counsel)

Meeting called to order at 2:05 PM by Sarah Chen.

Sarah Chen: Good afternoon everyone. Thank you for joining today's AI Advisory Committee meeting. Our primary agenda item is reviewing the progress of our AI Pilot project and discussing next steps. Jennifer, could you start us off with the current status?

Jennifer Walsh: Certainly, Sarah. I'm pleased to report that our AI pilot project has exceeded initial expectations. We've successfully deployed AI-powered tools for job posting generation and meeting minutes automation. User adoption has been strong with over 85% of HR staff actively using the job posting generator.

Dr. Michael Rodriguez: That's encouraging news. Have we conducted any bias audits on the job posting outputs? I want to ensure our AI isn't inadvertently introducing discriminatory language.

Jennifer Walsh: Excellent question, Michael. We completed our first bias audit last week. The results were largely positive, though we did identify some areas for improvement in inclusive language. We've already implemented corrective prompts to address these issues.

Alex Thompson: From a security perspective, I'm satisfied with the current implementation. All API communications are encrypted, and we're not storing sensitive data locally. However, I recommend we conduct quarterly security reviews as we expand the pilot.

Maria Santos: The public services department has been particularly interested in the meeting minutes generator. We have numerous community meetings, and this tool could significantly improve our documentation efficiency. When can we expect to expand access?

Sarah Chen: That's exactly what I wanted to discuss next. Based on the positive feedback, I'm proposing we move to phase two of the pilot. This would include expanding access to additional departments and introducing new AI capabilities.

David Kim: Before we expand, we need to ensure full compliance with municipal data governance policies. I've reviewed the current setup, and we're compliant, but expansion will require updated privacy impact assessments.

Dr. Michael Rodriguez: I agree with David's caution. We should also establish clear guidelines for AI use across departments. Not every use case is appropriate for AI automation.

Jennifer Walsh: I've prepared a preliminary expansion plan. Phase two would include the Parks and Recreation department, Public Works, and Finance. Each department would receive training and have designated AI coordinators.

Alex Thompson: What's our timeline looking like? I'll need at least two weeks to complete security assessments for the new departments.

Sarah Chen: I'm thinking a six-week rollout period. Week one and two for security and legal reviews, weeks three and four for training and setup, and weeks five and six for gradual deployment and monitoring.

Maria Santos: That timeline works well for us. We have quarterly budget reviews coming up where we could showcase the efficiency gains.

Dr. Michael Rodriguez: I'd like to propose we also establish an AI ethics review board as part of phase two. This would provide ongoing oversight and ensure responsible AI deployment.

David Kim: That's a sensible addition. The ethics board could report to this committee quarterly.

Jennifer Walsh: Should we set specific success metrics for phase two? I suggest tracking user adoption rates, time savings, and user satisfaction scores.

Sarah Chen: Absolutely. Let's aim for 75% adoption across pilot departments within the first month of deployment.

Alex Thompson: I'll prepare detailed security documentation for each new department. We should also establish incident reporting procedures specific to AI-related issues.

Maria Santos: One concern from my team - will there be sufficient technical support during the rollout? Our staff varies significantly in technical comfort levels.

Jennifer Walsh: We're planning comprehensive training sessions and will have dedicated support staff during the initial deployment period. We'll also create user guides and video tutorials.

Dr. Michael Rodriguez: I want to emphasize the importance of transparency with city employees about AI implementation. We should clearly communicate what AI is being used for and how it affects their work.

Sarah Chen: Excellent point. We'll develop a communication strategy that includes town halls and departmental briefings.

David Kim: From a legal standpoint, we'll need to update our employee handbook to include AI usage policies and guidelines.

Alex Thompson: I recommend we also implement audit logging for all AI interactions. This will help us track usage patterns and identify any potential issues.

Jennifer Walsh: The vendor has confirmed they can provide detailed usage analytics. This will help us optimize the tools based on actual usage patterns.

Maria Santos: What about cost projections for the expanded pilot? We'll need budget approval for the additional departments.

Sarah Chen: The current pilot has actually come in under budget due to efficiency gains. The expanded deployment should be cost-neutral with the savings from improved productivity.

Dr. Michael Rodriguez: That's promising, but we should also factor in training costs and potential productivity dips during the learning curve.

Jennifer Walsh: I've included those estimates in the budget projection. We're looking at approximately 15% temporary productivity decrease in the first month, followed by 25% productivity gains by month three.

Alex Thompson: Are we prepared for potential technical issues during expansion? We should have rollback procedures in place.

Sarah Chen: Good point. Let's establish clear criteria for pausing or rolling back the expansion if we encounter significant issues.

David Kim: I'll draft the legal framework for the rollback procedures and escalation protocols.

Maria Santos: When do we plan to present this expansion proposal to the City Council?

Sarah Chen: I'd like to present at the February council meeting. This gives us time to finalize all the documentation and address any remaining concerns.

Dr. Michael Rodriguez: Before we conclude, I want to ensure we're considering long-term implications. How does this pilot fit into our broader digital transformation strategy?

Jennifer Walsh: The AI pilot is a cornerstone of our five-year digital strategy. Success here will inform future AI implementations across all city operations.

Alex Thompson: We should also consider regional collaboration opportunities. Other municipalities have expressed interest in our approach.

Sarah Chen: That's an interesting point for future consideration. For now, let's focus on successful internal implementation.

Action Items Summary:
- Alex Thompson: Complete security assessments for new departments (Due: January 29)
- David Kim: Update privacy impact assessments and draft rollback procedures (Due: February 1)
- Dr. Michael Rodriguez: Establish AI ethics review board charter (Due: February 5)
- Jennifer Walsh: Finalize phase two implementation plan and training materials (Due: February 1)
- Maria Santos: Coordinate with department heads for readiness assessment (Due: January 25)
- Sarah Chen: Prepare City Council presentation materials (Due: February 10)

Next Meeting: February 12, 2025, 2:00 PM

Meeting adjourned at 3:28 PM.`;

  const handleSampleTranscript = () => {
    setValue('transcript', sampleTranscript);
    setValue('meetingTitle', 'AI Advisory Committee Meeting');
    setValue('meetingDate', '2025-01-15T14:00');
    setValue('attendees', 'Sarah Chen, Dr. Michael Rodriguez, Jennifer Walsh, Alex Thompson, Maria Santos, David Kim');
    setFileContent(sampleTranscript);
    toast.success('Sample transcript loaded successfully!');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'text/plain' || file.name.endsWith('.txt') || file.name.endsWith('.srt')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target.result;
          setFileContent(content);
          setValue('transcript', content);
          toast.success('File uploaded successfully!');
        };
        reader.readAsText(file);
      } else {
        toast.error('Please upload a .txt or .srt file');
      }
    }
  };

  const onSubmit = (data) => {
    const formData = {
      ...data,
      transcript: data.transcript || fileContent,
      selectedModel
    };
    onGenerate(formData);
  };

  const handleClear = () => {
    reset();
    setFileContent('');
    onClear();
    toast.success('Form cleared!');
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
        <h2 className="text-xl font-semibold flex items-center">
          <SafeIcon icon={FiFileText} className="mr-2" />
          Meeting Details
        </h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
        {/* Transcript Input */}
        <div className="space-y-2">
          <label htmlFor="transcript" className="block text-sm font-medium text-gray-700">
            Meeting Transcript *
          </label>
          <div className="space-y-3">
            <textarea
              id="transcript"
              rows={8}
              {...register('transcript', {
                required: 'Meeting transcript is required',
                minLength: {
                  value: 50,
                  message: 'Transcript must be at least 50 characters long'
                }
              })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
              placeholder="Paste your meeting transcript here or upload a file below..."
            />
            
            {/* File Upload and Sample Button */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* File Upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-purple-400 transition-colors">
                <div className="text-center">
                  <SafeIcon icon={FiUpload} className="mx-auto h-6 w-6 text-gray-400 mb-2" />
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <span className="text-sm text-purple-600 hover:text-purple-500 font-medium">
                      Upload transcript file
                    </span>
                    <input
                      id="file-upload"
                      type="file"
                      accept=".txt,.srt,text/plain"
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                  </label>
                  <p className="text-xs text-gray-500 mt-1">Supports .txt and .srt files</p>
                </div>
              </div>

              {/* Sample Transcript Button */}
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSampleTranscript}
                className="border-2 border-dashed border-green-300 rounded-lg p-4 hover:border-green-400 transition-colors bg-green-50 hover:bg-green-100"
              >
                <div className="text-center">
                  <SafeIcon icon={FiPlay} className="mx-auto h-6 w-6 text-green-600 mb-2" />
                  <span className="text-sm text-green-700 font-medium block">
                    Load Sample Transcript
                  </span>
                  <p className="text-xs text-green-600 mt-1">AI Advisory Committee Meeting</p>
                </div>
              </motion.button>
            </div>
          </div>
          {errors.transcript && (
            <p className="text-sm text-red-600">{errors.transcript.message}</p>
          )}
        </div>

        {/* Meeting Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="meetingTitle" className="block text-sm font-medium text-gray-700">
              <SafeIcon icon={FiFileText} className="inline mr-1" />
              Meeting Title
            </label>
            <input
              id="meetingTitle"
              type="text"
              {...register('meetingTitle')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              placeholder="e.g., Weekly Team Standup"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="meetingDate" className="block text-sm font-medium text-gray-700">
              <SafeIcon icon={FiCalendar} className="inline mr-1" />
              Date & Time
            </label>
            <input
              id="meetingDate"
              type="datetime-local"
              {...register('meetingDate')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="attendees" className="block text-sm font-medium text-gray-700">
            <SafeIcon icon={FiUsers} className="inline mr-1" />
            Attendees
          </label>
          <input
            id="attendees"
            type="text"
            {...register('attendees')}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
            placeholder="e.g., John Smith, Jane Doe, Mike Johnson"
          />
        </div>

        {/* Generation Options */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 flex items-center">
            <SafeIcon icon={FiSettings} className="mr-1" />
            Generation Options
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="tone" className="block text-sm font-medium text-gray-700">
                Tone
              </label>
              <select
                id="tone"
                {...register('tone')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              >
                <option value="formal">Formal</option>
                <option value="concise">Concise</option>
                <option value="action-oriented">Action-Oriented</option>
                <option value="detailed">Detailed</option>
              </select>
            </div>

            <div className="space-y-2">
              <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                Language
              </label>
              <select
                id="language"
                {...register('language')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
              >
                <option value="english">English</option>
                <option value="spanish">Spanish</option>
                <option value="french">French</option>
              </select>
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                id="includeActionItems"
                type="checkbox"
                {...register('includeActionItems')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <label htmlFor="includeActionItems" className="ml-2 block text-sm text-gray-700">
                Include Action Items
              </label>
            </div>

            <div className="flex items-center">
              <input
                id="includeSummary"
                type="checkbox"
                {...register('includeSummary')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <label htmlFor="includeSummary" className="ml-2 block text-sm text-gray-700">
                Include Executive Summary
              </label>
            </div>
          </div>
        </div>

        {/* Additional Notes */}
        <div className="space-y-2">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
            Additional Notes
          </label>
          <textarea
            id="notes"
            rows={3}
            {...register('notes')}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-none"
            placeholder="Any specific formatting requirements or additional context..."
          />
        </div>

        {/* LLM Selector */}
        <LLMSelector selectedModel={selectedModel} onModelChange={setSelectedModel} />

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4">
          <motion.button
            whileHover={{ scale: hasContent && !isGenerating ? 1.02 : 1 }}
            whileTap={{ scale: hasContent && !isGenerating ? 0.98 : 1 }}
            type="submit"
            disabled={!hasContent || isGenerating}
            className="flex-1 flex items-center justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating Minutes...
              </>
            ) : (
              <>
                <SafeIcon icon={FiRefreshCw} className="mr-2 h-4 w-4" />
                Generate Minutes
              </>
            )}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={handleClear}
            className="px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
          >
            <SafeIcon icon={FiTrash2} className="h-4 w-4" />
          </motion.button>
        </div>
      </form>
    </div>
  );
};

export default MeetingMinutesForm;